import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../theme";
import { api, connectWS } from "../api";
import Analytics from "../components/Analytics";
import Settings from "../components/Settings";
import Accounts from "../components/Accounts";
import EarningsWidget from "../components/EarningsWidget";
import { useUsdRate } from "../useUsdRate";
import Inventory from "../components/Inventory";
import TwoFactorSetup from "../components/TwoFactorSetup";

// Global animation styles
if (typeof document !== "undefined" && !document.getElementById("rbxwar-anims")) {
  const st = document.createElement("style");
  st.id = "rbxwar-anims";
  st.textContent = `
    @font-face { font-family: 'MADETommy'; src: url('/fonts/MADETommy.otf') format('opentype'); font-weight: 800; }
    @font-face { font-family: 'Moderniz';  src: url('/fonts/Moderniz.otf')  format('opentype'); }
    @font-face { font-family: 'Profa';     src: url('/fonts/Profa.ttf')     format('truetype'); font-weight: 900; }
    @font-face { font-family: 'Lumiare';   src: url('/fonts/Lumiare.otf')   format('opentype'); }

    .rw-nav-item {
      padding: 10px 20px;
      cursor: pointer;
      font-size: 13px;
      color: #888;
      font-family: 'Rubik', sans-serif;
      font-weight: 500;
      position: relative;
      overflow: hidden;
      transition: color 0.2s ease, background 0.2s ease;
      user-select: none;
    }
    .rw-nav-item::after {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle 60px at var(--mx,50%) var(--my,50%), rgba(255,255,255,0.06), transparent 70%);
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }
    .rw-nav-item:hover::after { opacity: 1; }
    .rw-nav-item:hover { color: #ccc; }
    .rw-nav-item.active { color: #fff; background: #1a1a1a; border-right: 2px solid var(--rbx-accent, #38bdf8); }
    .rw-nav-item:active { transform: scale(0.97); }

    .rw-magic-btn {
      position: relative;
      overflow: hidden;
      border: none;
      cursor: pointer;
      font-weight: 600;
      font-family: 'Rubik', sans-serif;
      transition: transform 0.15s cubic-bezier(.34,1.56,.64,1), box-shadow 0.2s ease, filter 0.2s ease;
    }
    .rw-magic-btn::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle 80px at var(--mx,50%) var(--my,50%), rgba(255,255,255,0.25), transparent 70%);
      opacity: 0;
      transition: opacity 0.25s ease;
      pointer-events: none;
      border-radius: inherit;
    }
    .rw-magic-btn:hover::before { opacity: 1; }
    .rw-magic-btn:hover {
      transform: translateY(-1px) scale(1.02);
      filter: brightness(1.1);
      box-shadow: 0 6px 24px rgba(0,0,0,0.4);
    }
    .rw-magic-btn:active {
      transform: scale(0.96) translateY(1px) !important;
      filter: brightness(0.95) !important;
      box-shadow: none !important;
      transition: transform 0.08s ease, filter 0.08s ease;
    }
    .rw-magic-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
      filter: none !important;
    }

    .rw-card {
      transition: transform 0.2s cubic-bezier(.34,1.56,.64,1), box-shadow 0.2s ease, border-color 0.2s ease;
    }
    .rw-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      border-color: #2a2a2a !important;
    }

    .rw-discord:hover { color: #5865f2 !important; background: #1e1e2e !important; transform: scale(1.1); }
    .rw-discord { transition: color 0.2s ease, background 0.2s ease, transform 0.2s cubic-bezier(.34,1.56,.64,1); }

    @keyframes logoGlow {
      0%   { filter: drop-shadow(0 0 4px rgba(255,255,255,0.06)); }
      50%  { filter: drop-shadow(0 0 8px rgba(255,255,255,0.14)); }
      100% { filter: drop-shadow(0 0 4px rgba(255,255,255,0.06)); }
    }
    .rw-logo {
      cursor: pointer;
      animation: logoGlow 4s ease-in-out infinite;
      transition: transform 0.6s cubic-bezier(0.19, 1, 0.22, 1), filter 0.2s ease;
      will-change: transform, filter;
    }
    .rw-logo:hover {
      transform: scale(1.08) rotate(-2deg);
      filter: drop-shadow(0 0 10px rgba(255,255,255,0.22)) brightness(1.06) !important;
      animation: none;
    }
    .rw-logo:active {
      transform: scale(0.97) rotate(0deg);
      transition: transform 0.1s ease;
    }

    @keyframes saleSlideIn {
      from { opacity: 0; transform: translateX(-16px) scale(0.95); }
      to   { opacity: 1; transform: translateX(0) scale(1); }
    }
    @keyframes saleGlow {
      0%   { box-shadow: 0 0 0 1px var(--acc), 0 0 22px -4px var(--acc); border-color: var(--acc); }
      100% { box-shadow: 0 0 0 0 transparent; }
    }

    .founder-rainbow {
      background: linear-gradient(90deg, #ff3b3b, #ff9900, #ffee00, #33ff55, #00d4ff, #a259ff, #ff3b3b);
      background-size: 300% 100%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent !important;
      font-weight: 800;
      animation: founderShift 4s linear infinite;
    }
    @keyframes founderShift {
      0%   { background-position: 0% 50%; }
      100% { background-position: 300% 50%; }
    }
  `;
  document.head.appendChild(st);
}

// Magic button — tracks mouse for spotlight effect
function MagicBtn({ children, onClick, style, disabled, className = "" }) {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  }, []);
  return (
    <button
      ref={ref}
      className={`rw-magic-btn ${className}`}
      style={style}
      onClick={onClick}
      disabled={disabled}
      onMouseMove={onMove}
    >
      {children}
    </button>
  );
}

// Nav item with spotlight effect
function NavItem({ children, active, onClick, disabled, badge }) {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  }, []);
  return (
    <div
      ref={ref}
      className={`rw-nav-item${active ? " active" : ""}`}
      onClick={disabled ? undefined : onClick}
      onMouseMove={disabled ? undefined : onMove}
      style={disabled ? { opacity: 0.4, cursor: "default", display: "flex", alignItems: "center", justifyContent: "space-between" } : undefined}
    >
      {children}
      {badge && <span style={{ fontSize: 9, letterSpacing: 0.5, color: "#666", border: "1px solid #333", borderRadius: 4, padding: "2px 5px", marginLeft: 8 }}>{badge}</span>}
    </div>
  );
}

const NAV = ["Dashboard", "Inventory", "Analytics", "Calculator", "Settings", "Accounts", "Help"];
const DISCORD_URL = "https://discord.gg/683FrRFgsK";
const UPGRADE_URL = "https://rbxwar.mysellauth.com/";

function PrivacyModal({ onClose }) {
  return (
    <div style={pm.overlay} onClick={onClose}>
      <div style={pm.box} onClick={e => e.stopPropagation()}>
        <div style={pm.header}>
          <div style={pm.title}>Privacy Policy</div>
          <button style={pm.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={pm.body}>
          <p style={pm.p}>
            RBXWar ("we", "us", "our") provides an automated pricing tool for Roblox limited items.
            This policy explains how your information is stored and used.
          </p>

          <div style={pm.h2}>Your Security</div>
          <p style={pm.p}>
            Your Roblox <code style={pm.code}>.ROBLOSECURITY</code> cookie and 2FA secret are{" "}
            <b style={{color:accent}}>encrypted before being stored</b>, never saved in plain text.
            Your cookie is masked wherever it would otherwise be shown in the dashboard. Because losing
            your license key would otherwise mean losing access to your account, every account gets a
            recovery phrase the first time it's activated, a backup way to log in if you ever lose your key.
          </p>

          <div style={pm.h2}>How Your Cookie Is Used</div>
          <p style={pm.p}>
            Used exclusively to authenticate as your Roblox account when the bot runs. Never sold or
            shared with third parties.
          </p>

          <div style={pm.h2}>Data Retention</div>
          <p style={pm.p}>
            Your data is retained while your account is active. Removing an account deletes its
            cookie and associated data. Want everything deleted? Contact us below.
          </p>

          <div style={pm.h2}>Third Parties</div>
          <p style={pm.p}>
            We use Rolimons and RoUtility's public APIs for item values and pricing, and Roblox's
            own public APIs. We don't sell data to advertisers or run ads.
          </p>

          <div style={pm.h2}>Contact</div>
          <p style={pm.p}>
            Questions? Our{" "}
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" style={pm.link}>Discord server</a>{" "}
            is the only place we offer support. Join and open a ticket there.
          </p>
        </div>
      </div>
    </div>
  );
}

const pm = {
  overlay:  { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "fadeUp 0.2s ease both" },
  box:      { background: "#0e0e0e", border: "1px solid #1a1a1a", borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "calc(80vh / var(--ui-zoom, 1))", display: "flex", flexDirection: "column", overflow: "hidden" },
  header:   { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "1px solid #1a1a1a", flexShrink: 0 },
  title:    { color: "#fff", fontSize: 16, fontWeight: 700 },
  closeBtn: { background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, color: "#888", width: 30, height: 30, cursor: "pointer", fontSize: 14 },
  body:     { padding: "20px 22px", overflowY: "auto" },
  h2:       { color: "var(--rbx-accent, #38bdf8)", fontSize: 13, fontWeight: 700, margin: "18px 0 8px" },
  p:        { color: "#aaa", fontSize: 13, lineHeight: 1.7, margin: "0 0 4px" },
  code:     { background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 4, padding: "1px 5px", fontFamily: "monospace", fontSize: 12, color: "var(--rbx-accent, #38bdf8)" },
  link:     { color: "var(--rbx-accent, #38bdf8)" },
};

// Splits a log line on "Inventory" and renders it as a clickable, underlined
// link (switching to the Inventory tab) whenever a message mentions it —
// e.g. "Set a custom floor % on the Inventory page."
function renderLogMessage(msg, onGoInventory) {
  if (!msg || !msg.includes("Inventory")) return msg;
  const parts = msg.split("Inventory");
  return parts.reduce((acc, part, i) => {
    acc.push(part);
    if (i < parts.length - 1) {
      acc.push(
        <span
          key={i}
          onClick={(e) => { e.stopPropagation(); onGoInventory(); }}
          style={{ textDecoration: "underline", cursor: "pointer", color: accent }}
        >
          Inventory
        </span>
      );
    }
    return acc;
  }, []);
}

function getTierInfo(tier, createdAt) {
  const t = (tier || "").toLowerCase();
  const displayTier = t ? t.toUpperCase() : "NO PLAN";
  const activatedStr = createdAt && !isNaN(new Date(createdAt).getTime())
    ? new Date(createdAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  if (!t) return { displayTier, timeLeft: null, activatedStr, isLifetime: false, expired: false };
  if (t.includes("lifetime")) return { displayTier, timeLeft: "Lifetime", activatedStr, isLifetime: true, expired: false };

  if (!createdAt) return { displayTier, timeLeft: null, activatedStr, isLifetime: false, expired: false };
  const start = new Date(createdAt);
  if (isNaN(start.getTime())) return { displayTier, timeLeft: null, activatedStr, isLifetime: false, expired: false };

  const expires = start.getTime() + 30 * 24 * 60 * 60 * 1000;
  const msLeft = expires - Date.now();
  if (msLeft <= 0) return { displayTier, timeLeft: "Expired", activatedStr, isLifetime: false, expired: true };

  const totalMinutes = Math.floor(msLeft / (60 * 1000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const timeLeft = `${days}d ${hours}h ${minutes}m left`;

  return { displayTier, timeLeft, activatedStr, isLifetime: false, expired: false };
}

// ── Onboarding modals ──────────────────────────────────────────────────────
// 1) UsernameModal — blocking. Every user needs a username so support can
//    tell who they are in logs and tickets; it can't be dismissed.
// 2) SecureAccountModal — also blocking. Every account needs a password and
//    authenticator 2FA (QR code + backup codes); that's what the username +
//    password login checks.
function UsernameModal({ token, onSaved, onLogout }) {
  const { accent } = useTheme();
  const [name, setName]     = useState("");
  const [error, setError]   = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setError("");
    const n = name.trim();
    if (n.length < 3) return setError("Username must be at least 3 characters.");
    if (!/^[A-Za-z0-9_. -]{3,15}$/.test(n)) return setError("Use letters, numbers, spaces, _ . - only (max 15).");
    setSaving(true);
    try {
      const res = await api.setDisplayName(token, n);
      onSaved(res.display_name || n);
    } catch (e) {
      if (e.sessionExpired) return;
      setError(e.message === "USERNAME_TAKEN" ? "That username is taken. Try another." : (e.message || "Failed to save. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={mstyles.overlay}>
      <div style={mstyles.box}>
        <div style={{ ...mstyles.iconCircle, background: `${accent}22`, color: accent }}>👤</div>
        <h2 style={mstyles.title}>Choose a username</h2>
        <p style={mstyles.sub}>
          Everyone needs a username. It's how we recognise you in logs and support
          tickets so we can actually help when something breaks.
        </p>
        <input
          placeholder="Username (3-15 characters)"
          value={name}
          maxLength={15}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          style={mstyles.input}
          autoFocus
        />
        {error && <div style={mstyles.error}>{error}</div>}
        <button style={{ ...mstyles.saveBtn, background: accent }} onClick={submit} disabled={saving}>
          {saving ? "Saving..." : "Continue"}
        </button>
        <button style={mstyles.skipBtn} onClick={onLogout}>Log out</button>
      </div>
    </div>
  );
}

function SecureAccountModal({ token, hasPassword, isAdmin, onSecured, onLogout }) {
  const { accent } = useTheme();
  return (
    <div style={mstyles.overlay}>
      <div style={{ ...mstyles.box, maxHeight: "calc(92vh / var(--ui-zoom, 1))", overflowY: "auto" }}>
        <div style={{ ...mstyles.iconCircle, background: `${accent}22`, color: accent }}>🔒</div>
        <h2 style={mstyles.title}>Secure your account</h2>
        <p style={mstyles.sub}>
          {isAdmin
            ? "Your admin key login will now ask for a 6-digit code from an authenticator app, so the key alone can't get into the admin account."
            : "Every RBXWar account needs a password and 2FA. You'll log in with your username, your password and a 6-digit code from an authenticator app."}
        </p>
        <TwoFactorSetup token={token} accent={accent} hasPassword={hasPassword} onDone={onSecured} />
        <button style={mstyles.skipBtn} onClick={onLogout}>Log out</button>
      </div>
    </div>
  );
}

const mstyles = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  box:       { background: "#0e0e0e", border: "1px solid #1e1e1e", borderRadius: 14, padding: "36px 34px", width: 420, maxWidth: "100%", textAlign: "center" },
  iconCircle:{ width: 52, height: 52, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 18px" },
  title:     { color: "#fff", fontSize: 20, fontWeight: 800, margin: "0 0 10px" },
  sub:       { color: "#999", fontSize: 13.5, lineHeight: 1.6, margin: "0 0 16px" },
  noteBox:   { background: "#141414", border: "1px solid #262626", borderRadius: 8, padding: "12px 14px", color: "#888", fontSize: 12.5, lineHeight: 1.6, textAlign: "left", marginBottom: 20 },
  input:     { width: "100%", boxSizing: "border-box", background: "#141414", border: "1px solid #222", borderRadius: 8, padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none", marginBottom: 10, fontFamily: "inherit" },
  error:     { color: "#f87171", fontSize: 12.5, marginBottom: 10, textAlign: "left" },
  saveBtn:   { width: "100%", padding: "13px 0", border: "none", borderRadius: 8, color: "#000", fontSize: 14.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginTop: 4 },
  skipBtn:   { width: "100%", background: "none", border: "none", color: "#444", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", marginTop: 12 },
};

const API_URL = process.env.REACT_APP_API_URL || "";

function Calculator({ token }) {
  const { accent, accentBg } = useTheme();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [manualRap, setManualRap] = useState("");
  const [manualValue, setManualValue] = useState("");
  const [mode, setMode] = useState("value");
  const [pct, setPct] = useState("5");
  const [fixedR, setFixedR] = useState("");
  // Starts at the rate saved in Settings; typing here only changes the calculator.
  const savedRate = useUsdRate(token);
  const [rateEdit, setRateEdit] = useState(null);
  const rate = rateEdit !== null ? rateEdit : String(savedRate);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!query.trim() || selected) { setResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${API_URL}/public/item-search?q=${encodeURIComponent(query.trim())}`)
        .then(r => r.json()).then(d => setResults(d.items || [])).catch(() => {});
    }, 150);
    return () => clearTimeout(t);
  }, [query, selected]);

  const filtered = results;

  const baseRap   = parseInt(manualRap, 10) || 0;
  const baseValue = parseInt(manualValue, 10) || 0;
  const base      = mode === "value" ? baseValue : baseRap;

  let salePrice = 0;
  if (mode === "fixed") {
    salePrice = parseInt(fixedR, 10) || 0;
  } else if (base > 0) {
    const p = parseFloat(pct) || 0;
    salePrice = Math.max(1, Math.round(base * (1 - p / 100)));
  }

  const robuxEarned = Math.floor(salePrice * 0.70);
  const usdEarned   = robuxEarned > 0 ? (robuxEarned / 1000) * (parseFloat(rate) || 0) : 0;
  const hasResult   = salePrice > 0 && robuxEarned > 0;

  const cs = {
    wrap:    { padding: "32px 36px", maxWidth: 640, color: "#fff" },
    label:   { fontSize: 11, color: "#444", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, fontFamily: "'Moderniz',sans-serif" },
    card:    { background: "#0f0f0f", border: "1px solid #1a1a1a", borderRadius: 12, padding: "20px 24px", marginBottom: 16 },
    row:     { display: "flex", gap: 12 },
    inp:     { flex: 1, background: "#141414", border: "1px solid #222", borderRadius: 8, padding: "11px 14px", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
    toggle:  { display: "flex", background: "#111", borderRadius: 8, padding: 3, gap: 4, marginBottom: 16 },
    toggleB: (active) => ({ flex: 1, padding: "8px 0", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: active ? "#1e1e1e" : "transparent", color: active ? accent : "#555", transition: "all 0.15s" }),
    result:  { background: accentBg, border: `1px solid ${accent}22`, borderRadius: 12, padding: "24px 28px", marginTop: 8 },
    bigNum:  { fontSize: "clamp(28px,4vw,40px)", fontWeight: 800, color: accent, fontFamily: "'Profa',sans-serif", lineHeight: 1 },
    subNum:  { fontSize: 13, color: "#888", marginTop: 4 },
    dropWrap:{ position: "relative" },
    drop:    { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, zIndex: 50, overflow: "hidden" },
    dropItem:(hover) => ({ padding: "10px 14px", cursor: "pointer", fontSize: 13, color: hover ? "#fff" : "#ccc", background: hover ? "#222" : "transparent", display: "flex", justifyContent: "space-between", alignItems: "center" }),
  };

  return (
    <div style={cs.wrap}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ color: "#fff", fontSize: 22, fontWeight: 800, margin: "0 0 6px" }}>Sale Calculator</h2>
        <p style={{ color: "#444", fontSize: 14, margin: 0 }}>figure out what you'll actually pocket from a sale.</p>
      </div>

      {/* Buy rate (USD per 1k Robux) */}
      <div style={cs.card}>
        <div style={cs.label}>buy rate</div>
        <div style={cs.row}>
          <span style={{ display: "flex", alignItems: "center", color: "#555", fontSize: 14 }}>$</span>
          <input style={cs.inp} type="number" min="0" step="0.01" placeholder="5.00" value={rate} onChange={e => setRateEdit(e.target.value)} />
          <div style={{ display: "flex", alignItems: "center", color: "#555", fontSize: 14, whiteSpace: "nowrap" }}>per 1,000 R$</div>
        </div>
      </div>

      {/* Item picker */}
      <div style={cs.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={cs.label}>item</div>
          {(selected || query || manualRap || manualValue) && <button onClick={() => { setSelected(null); setQuery(""); setManualRap(""); setManualValue(""); }} style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 12, fontFamily: "inherit", whiteSpace: "nowrap", padding: 0 }}>clear</button>}
        </div>
        <div style={cs.dropWrap}>
          <input
            style={cs.inp}
            placeholder="search your inventory or type any name..."
            value={selected ? selected.name : query}
            onChange={e => { setQuery(e.target.value); setSelected(null); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          />
          {showDropdown && filtered.length > 0 && (
            <div style={cs.drop}>
              {filtered.map(it => (
                <ItemRow key={it.id} it={it} accent={accent} onSelect={() => { setSelected(it); setManualRap(String(it.rap || "")); setManualValue(String(it.value || "")); setQuery(""); setShowDropdown(false); setResults([]); }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "center" }}>
          <div style={{ fontSize: 12, color: "#444", whiteSpace: "nowrap", width: 64 }}>RAP</div>
          <input
            style={{ ...cs.inp, marginBottom: 0 }}
            type="number"
            min="1"
            placeholder="enter RAP manually"
            value={manualRap}
            onChange={e => setManualRap(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "center" }}>
          <div style={{ fontSize: 12, color: "#444", whiteSpace: "nowrap", width: 64 }}>Roli value</div>
          <input
            style={{ ...cs.inp, marginBottom: 0 }}
            type="number"
            min="1"
            placeholder="enter Rolimons value manually"
            value={manualValue}
            onChange={e => setManualValue(e.target.value)}
          />
        </div>
      </div>

      {/* Price mode */}
      <div style={cs.card}>
        <div style={cs.label}>sell at</div>
        <div style={cs.toggle}>
          <button style={cs.toggleB(mode === "value")} onClick={() => setMode("value")}>% under value</button>
          <button style={cs.toggleB(mode === "rap")} onClick={() => setMode("rap")}>% under RAP</button>
          <button style={cs.toggleB(mode === "fixed")} onClick={() => setMode("fixed")}>fixed robux</button>
        </div>
        {mode !== "fixed" ? (
          <div style={cs.row}>
            <input style={cs.inp} type="number" min="0" max="100" placeholder="e.g. 5" value={pct} onChange={e => setPct(e.target.value)} />
            <div style={{ display: "flex", alignItems: "center", color: "#555", fontSize: 14, whiteSpace: "nowrap" }}>{mode === "value" ? "% under Roli value" : "% under RAP"}</div>
          </div>
        ) : (
          <input style={cs.inp} type="number" min="1" placeholder="sale price in R$" value={fixedR} onChange={e => setFixedR(e.target.value)} />
        )}
        {mode !== "fixed" && base > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#555" }}>
            → sale price: <span style={{ color: "#aaa" }}>{salePrice.toLocaleString()} R$</span>
          </div>
        )}
        {mode !== "fixed" && base === 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
            {mode === "value" ? "Pick an item or enter its Roli value above. Items with no Roli value can use % under RAP." : "Pick an item or enter its RAP above."}
          </div>
        )}
      </div>

      {/* Results */}
      {hasResult && (
        <div style={cs.result}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div>
              <div style={{ fontSize: 11, color: accent + "99", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontFamily: "'Moderniz',sans-serif" }}>you receive</div>
              <div style={cs.bigNum}>{robuxEarned.toLocaleString()}</div>
              <div style={cs.subNum}>R$ after 30% fee</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: accent + "99", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, fontFamily: "'Moderniz',sans-serif" }}>USD value</div>
              <div style={cs.bigNum}>${usdEarned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div style={cs.subNum}>at ${parseFloat(rate) || 0} / 1k R$</div>
            </div>
          </div>
          <div style={{ marginTop: 20, padding: "12px 0 0", borderTop: "1px solid " + accent + "22", fontSize: 12, color: "#555", display: "flex", gap: 24 }}>
            <span>sale price <span style={{ color: "#888" }}>{salePrice.toLocaleString()} R$</span></span>
            <span>roblox takes <span style={{ color: "#888" }}>{(salePrice - robuxEarned).toLocaleString()} R$ (30%)</span></span>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemRow({ it, accent, onSelect }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onSelect}
      style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, color: hover ? "#fff" : "#ccc", background: hover ? "#222" : "transparent", display: "flex", justifyContent: "space-between", alignItems: "center" }}
    >
      <span>{it.name}</span>
      <span style={{ color: hover ? accent : "#555", fontSize: 12 }}>{(it.rap || 0).toLocaleString()} R$</span>
    </div>
  );
}

const FAQ = [
  { q: "Reseller fetch errors in my logs", a: "Go to Settings → Bot and turn down your Item delay and Loop delay. Pro accounts can go as low as 5s item delay / 15s loop delay; other plans bottom out at 10s / 30s. Hit Save at the bottom, then restart the bot." },
  { q: "My account is linked but my items aren't being listed", a: "That's a 2FA issue. Go to Accounts, find the account and click \"+ 2FA\" (or \"Edit 2FA\" if one is already set). Paste your authenticator's 2FA secret key, which is the long Base32 setup key shown when you added the authenticator app, not the 6-digit code. Save it, then restart the bot." },
  { q: "How do I log in with a username and password instead of my key?", a: "The first time you open your dashboard, pick a username, then set a password and turn on 2FA: scan the QR code with an authenticator app (Google Authenticator, Authy, 1Password), type the 6-digit code, and save your backup codes. After that, on the login page choose the Username tab and enter your username, password and the current 6-digit code. Lost your phone? Use one of your backup codes, or log in with your license key." },
  { q: "Bot logs say 'Proxy unreachable'", a: "The proxy on that account is failing: \"407\" means the proxy username/password is wrong, and \"connection refused\" means the proxy itself is down. Go to Accounts, add a working proxy to your proxy list (format: user:pass@ip:port) and assign it to the account, then restart the bot." },
  { q: "Logs say 'all copies on hold'", a: "Roblox holds an item for a short time after you buy or receive it, and you can't list it during that window. Nothing to fix. The bot picks it up automatically once the hold ends." },
];

function HelpTab({ token }) {
  const { accent } = useTheme();
  const [question, setQuestion] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [mine, setMine] = useState([]);

  const loadMine = () => api.supportMine(token).then(r => setMine(r.tickets || [])).catch(() => {});
  useEffect(() => { loadMine(); }, []);

  const submit = async () => {
    if (!question.trim()) return;
    setSending(true); setError("");
    try {
      await api.supportAsk(token, question.trim());
      setSent(true);
      setQuestion("");
      loadMine();
    } catch (e) {
      if (!e.sessionExpired) setError(e.message || "Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 0" }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Help & Support</div>
      <div style={{ fontSize: 14, color: "#666", marginBottom: 32 }}>Common issues and how to fix them, or send us a question directly.</div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>FAQ</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 40 }}>
        {FAQ.map((item, i) => (
          <div key={i} style={{ background: "#111", border: "1px solid #222", borderRadius: 10, overflow: "hidden" }}>
            <div
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              style={{ padding: "14px 18px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <span style={{ fontSize: 14, color: openFaq === i ? "#fff" : "#ccc", fontWeight: 500 }}>{item.q}</span>
              <span style={{ color: accent, fontSize: 18, lineHeight: 1, marginLeft: 12, flexShrink: 0 }}>{openFaq === i ? "−" : "+"}</span>
            </div>
            {openFaq === i && (
              <div style={{ padding: "0 18px 16px", fontSize: 13, color: "#999", lineHeight: 1.65, borderTop: "1px solid #1a1a1a" }}>
                <div style={{ paddingTop: 12 }}>{item.a}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Contact Support</div>
      {sent && (
        <div style={{ background: "#0d1f0d", border: "1px solid #1a4a1a", borderRadius: 10, padding: "14px 20px", color: "#4ade80", fontSize: 14, marginBottom: 12 }}>
          ✓ Question sent! Your reply will show up under "Your questions" below.
        </div>
      )}
      {(
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 10, padding: "20px 24px" }}>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Describe your issue or ask a question…"
            rows={4}
            style={{ width: "100%", background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 8, color: "#fff", fontSize: 14, padding: "12px", resize: "vertical", outline: "none", fontFamily: "Rubik, sans-serif", boxSizing: "border-box" }}
          />
          {error && <div style={{ color: "#f87171", fontSize: 13, marginTop: 8 }}>{error}</div>}
          <button
            className="rw-magic-btn"
            onClick={submit}
            disabled={sending || !question.trim()}
            style={{ marginTop: 12, background: accent, color: "#000", padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600 }}
          >
            {sending ? "Sending…" : "Send Question"}
          </button>
        </div>
      )}

      {mine.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em", margin: "40px 0 14px" }}>Your questions</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {mine.map(t => (
              <div key={t.id} style={{ background: "#111", border: "1px solid #222", borderRadius: 10, padding: "14px 18px" }}>
                <div style={{ fontSize: 13, color: "#ccc", lineHeight: 1.6 }}>{t.question}</div>
                {t.answer ? (
                  <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: `2px solid ${accent}`, fontSize: 13, color: accent, lineHeight: 1.6 }}>{t.answer}</div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>Waiting for a reply…</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard({ token, userId, tier, createdAt, isAdmin, onLogout, onAdmin, theme, onThemeChange }) {
  const { accent, accentBg, accentBorder, name: themeName } = useTheme();
  const styles = getStyles(accent, accentBg, accentBorder);
  const usdRate = useUsdRate(token);

  const [tab, setTab]         = useState("Dashboard");

  // Onboarding — both steps are required: a username, then a password + 2FA.
  const [sec, setSec] = useState(null); // null until the server answers, avoids a flash of the modal

  useEffect(() => {
    api.getSecurityStatus(token)
      .then(res => setSec({ hasUsername: !!res.has_username, hasPassword: !!res.has_password, has2fa: !!res.has_2fa, isAdmin: !!res.is_admin_account }))
      .catch(() => {}); // fail quiet: never block the dashboard if we can't tell the status
  }, [token]);
  const [status, setStatus]   = useState({});
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTierInfo, setShowTierInfo] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [limits, setLimits]   = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [salesLoaded, setSalesLoaded] = useState(false);
  const [salesWarn, setSalesWarn] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth <= 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const logsRef               = useRef(null);
  const wsRef                 = useRef(null);
  const prevSaleIdsRef        = useRef(new Set());

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close the drawer automatically after picking a tab on mobile
  const selectTab = (n) => {
    setTab(n);
    if (isMobile) setSidebarOpen(false);
  };

  const fetchStatus = async () => {
    try {
      const s = await api.botStatus(token);
      setStatus(s);
      setLogs(s.logs || []);
    } catch (e) {
      if (e.subscriptionExpired) { onLogout("EXPIRED"); return; }
      if (e.sessionExpired) { onLogout(); return; }
      console.error("Failed to fetch bot status:", e.message);
    }
  };

  const fetchRecentSales = async () => {
    try {
      const res = await api.recentSales(token);
      setRecentSales((res.sales || []).slice(0, 10));
      setSalesWarn((res.warnings || []).length > 0);
    } catch (e) {
      if (e.sessionExpired) { onLogout(); return; }
      try {
        // older backend without /sales/recent — fall back to saved sales
        const a = await api.analytics(token);
        setRecentSales((a.recent_sales || []).slice(0, 10));
      } catch (e2) {
        if (e2.sessionExpired) { onLogout(); return; }
        console.error("Failed to fetch recent sales:", e2.message);
      }
    } finally {
      setSalesLoaded(true);
    }
  };

  useEffect(() => {
    api.accountLimits(token).then(setLimits).catch(() => {});
  }, []);

  useEffect(() => {
    fetchRecentSales();
    const interval = setInterval(fetchRecentSales, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    prevSaleIdsRef.current = new Set(recentSales.map(s => s.id));
  }, [recentSales]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);

    // WebSocket for live logs
    wsRef.current = connectWS(userId, token, (msg) => {
      if (msg.type === "log") {
        setLogs(prev => [...prev.slice(-199), msg]);
      }
    });

    // Safari (especially mobile) throttles setInterval and can silently
    // kill the WebSocket when the tab is backgrounded for a while, with no
    // built-in way to recover. When the tab becomes visible/focused again,
    // force an immediate status refresh and reconnect the socket if it died.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchStatus();
        wsRef.current?.reconnectIfNeeded();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      wsRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs]);

  const botStart = async () => {
    setLoading(true);
    try { await api.botStart(token); await fetchStatus(); }
    catch (e) {
      if (e.sessionExpired) { onLogout(); return; }
      alert(e instanceof TypeError ? "Can't reach the server right now. Try again in a minute." : e.message);
    }
    finally { setLoading(false); }
  };

  const botStop  = async () => { await api.botStop(token); fetchStatus(); };
  const botPause = async () => {
    if (status.paused) await api.botResume(token);
    else await api.botPause(token);
    fetchStatus();
  };

  const stats = status.stats || {};
  const robuxUSD = ((stats.robux || 0) / 1000 * usdRate).toFixed(2);
  const pendingUSD = ((stats.pending || 0) / 1000 * usdRate).toFixed(2);
  const totalUSD = (((stats.robux || 0) + (stats.pending || 0)) / 1000 * usdRate).toFixed(2);

  return (
    <div style={styles.wrap}>
      {isMobile && (
        <>
          <button style={styles.hamburger} onClick={() => setSidebarOpen(o => !o)}>
            {sidebarOpen ? "✕" : "☰"}
          </button>
          {sidebarOpen && (
            <div style={styles.backdrop} onClick={() => setSidebarOpen(false)} />
          )}
        </>
      )}

      {/* Sidebar */}
      <div style={{
        ...styles.sidebar,
        ...(isMobile ? {
          position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 200,
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.25s ease",
          boxShadow: sidebarOpen ? "8px 0 32px rgba(0,0,0,0.5)" : "none",
        } : {}),
      }}>
        <div style={styles.logoWrap} onClick={() => selectTab("Dashboard")}>
          <img src="/logo.png" alt="RBXWar" className="rw-logo" style={{ ...styles.logoImg, height:56, width:"auto", objectFit:"contain", filter: {
            green:     "grayscale(1) sepia(1) hue-rotate(102deg) saturate(4) brightness(0.95)",
            blue:      "grayscale(1) sepia(1) hue-rotate(157deg) saturate(3) brightness(0.95)",
            halloween: "grayscale(1) sepia(1) hue-rotate(345deg) saturate(4) brightness(1.0)",
            kawaii:    "grayscale(1) sepia(1) hue-rotate(280deg) saturate(3) brightness(1.0)",
            mono:      "grayscale(1) brightness(1.6)",
          }[themeName] || "none" }} />
        </div>
        <div style={styles.tierBadge} className={isAdmin ? "founder-rainbow" : ""}>
          {isAdmin ? "FOUNDER" : tier.toUpperCase()}
        </div>
        {NAV.map(n => (
          <NavItem
            key={n}
            active={tab === n}
            onClick={() => selectTab(n)}
            disabled={n === "Analytics"}
            badge={n === "Analytics" ? "COMING SOON" : null}
          >
            {n}
          </NavItem>
        ))}
        <div style={{flex: 1}} />
        <EarningsWidget token={token} />
        {isAdmin && <NavItem onClick={onAdmin}>Admin</NavItem>}
        <NavItem onClick={onLogout}><span style={{color:"#ff4444"}}>Logout</span></NavItem>

        <div style={styles.bottomRow}>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.discordIcon}
            className="rw-discord"
            title="Join our Discord"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.099.246.197.373.291a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.892.076.076 0 0 0-.041.106c.36.698.772 1.362 1.225 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.057c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.027zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.096 2.157 2.42 0 1.333-.955 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
          </a>

          <a
            href={UPGRADE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.upgradeIcon}
            className="rw-logo"
            onMouseEnter={() => setShowTierInfo(true)}
            onMouseLeave={() => setShowTierInfo(false)}
            title="Upgrade your plan"
          >
            <img src="/upgrade-icon.png" alt="Upgrade" style={styles.upgradeIconImg} />
            {showTierInfo && (() => {
              const info = getTierInfo(tier, createdAt);
              return (
                <div style={styles.tierTooltip}>
                  <div style={styles.tierTooltipTier}>{info.displayTier}</div>
                  {info.timeLeft && (
                    <div style={{...styles.tierTooltipRow, color: info.expired ? "#ef4444" : accent}}>
                      {info.timeLeft}
                    </div>
                  )}
                  {info.activatedStr && !info.isLifetime && (
                    <div style={styles.tierTooltipSub}>Activated {info.activatedStr}</div>
                  )}
                  <div style={styles.tierTooltipUpgrade}>Click to upgrade →</div>
                </div>
              );
            })()}
          </a>

          <div
            style={styles.tierLetter}
            onClick={() => setShowPrivacyModal(true)}
            title="Privacy Policy"
          >
            P
          </div>
        </div>
      </div>

      {showPrivacyModal && (
        <PrivacyModal onClose={() => setShowPrivacyModal(false)} />
      )}

      {/* Content */}
      <div style={{...styles.main, ...(isMobile ? {padding: "64px 16px 16px", minWidth: 0} : {})}}>
        {tab === "Dashboard" && (
          <div style={{ display:"flex", gap:20, height:"100%", minHeight:0 }}>
            {/* Left column */}
            <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:20 }}>

              {/* Robux bar */}
              <div style={styles.robuxBar}>
                <div style={styles.robuxRow}>
                  <span style={styles.robuxLabel}>Robux</span>
                  <span style={styles.robuxVal}>{(stats.robux||0).toLocaleString()}</span>
                  <span style={styles.robuxSep}>·</span>
                  <span style={styles.robuxLabel}>Pending</span>
                  <span style={styles.robuxVal}>{(stats.pending||0).toLocaleString()}</span>
                  <span style={styles.robuxSep}>·</span>
                  <span style={styles.robuxLabel}>Total</span>
                  <span style={styles.robuxVal}>{((stats.robux||0)+(stats.pending||0)).toLocaleString()}</span>
                </div>
                <div style={styles.robuxRow}>
                  <span style={styles.robuxLabel}>USD</span>
                  <span style={styles.robuxUSD}>${robuxUSD}</span>
                  <span style={styles.robuxSep}>·</span>
                  <span style={styles.robuxLabel}>Pending</span>
                  <span style={styles.robuxUSD}>${pendingUSD}</span>
                  <span style={styles.robuxSep}>·</span>
                  <span style={styles.robuxLabel}>Total</span>
                  <span style={styles.robuxUSD}>${totalUSD}</span>
                </div>
              </div>

              {/* Stats cards */}
              <div style={styles.cards}>
                {[
                  ["Loops", stats.loops || 0],
                  ["Scanned", stats.items_scanned || 0],
                  ["Listed", stats.items_listed || 0],
                  ["Status", status.running ? (status.paused ? "PAUSED" : "RUNNING") : "STOPPED"],
                ].map(([label, val]) => (
                  <div key={label} style={styles.card} className="rw-card">
                    <div style={styles.cardLabel}>{label}</div>
                    <div style={styles.cardVal}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Controls */}
              <div style={styles.controls}>
                {!status.running ? (
                  <MagicBtn style={{...styles.btn, background: accent}} onClick={botStart} disabled={loading}>
                    {loading ? "Starting..." : "▶ Start Bot"}
                  </MagicBtn>
                ) : (
                  <>
                    <MagicBtn style={{...styles.btn, background: status.paused ? accent : "#f59e0b"}} onClick={botPause}>
                      {status.paused ? "▶ Resume" : "⏸ Pause"}
                    </MagicBtn>
                    <MagicBtn style={{...styles.btn, background: "#ef4444"}} onClick={botStop}>
                      ■ Stop
                    </MagicBtn>
                  </>
                )}
              </div>

              {/* Live logs */}
              <div style={{...styles.logsWrap, flex:1, marginBottom:0}}>
                <div style={styles.logsHeader}>Live Logs</div>
                <div style={styles.logs} ref={logsRef}>
                  {logs.map((l, i) => (
                    <div key={i} style={styles.logLine}>
                      <span style={styles.logTs}>{new Date((l.ts||Date.now())*1000).toLocaleTimeString()}</span>
                      <span style={{color: l.msg?.includes("[-]") ? "#ff6b6b" : l.msg?.includes("[+]") ? accent : "#aaa"}}>
                        {renderLogMessage(l.msg, () => setTab("Inventory"))}
                      </span>
                    </div>
                  ))}
                  {!logs.length && <div style={{color:"#444", padding:8}}>No logs yet. Start the bot</div>}
                </div>
              </div>
            </div>

            {/* Right column — recent sales */}
            <div style={{ width:260, flexShrink:0, display:"flex", flexDirection:"column", minHeight:0 }}>
              <div style={{ fontSize:11, color:"#444", letterSpacing:2, textTransform:"uppercase", marginBottom:14, fontFamily:"'Moderniz',sans-serif" }}>Recent Sales</div>
              <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 }}>
                {recentSales.length === 0 && (
                  <div style={{color:"#333", fontSize:13, paddingTop:4}}>{salesLoaded ? "No sales yet" : "Loading your sales…"}</div>
                )}
                {recentSales.map(s => {
                  const isNew = !prevSaleIdsRef.current.has(s.id);
                  const usd = ((s.amount || 0) / 1000 * usdRate).toFixed(2);
                  const timeStr = s.created_at ? (() => {
                    const diff = Math.max(0, Math.floor((Date.now() - new Date(s.created_at).getTime()) / 1000));
                    if (diff < 60) return `${diff}s ago`;
                    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
                    if (diff < 172800) return `${Math.floor(diff/3600)}h ago`;
                    return `${Math.floor(diff/86400)}d ago`;
                  })() : "";
                  return (
                    <div key={s.id} style={{
                      "--acc": accent,
                      background:"#0f0f0f", border:"1px solid #1a1a1a", borderRadius:10,
                      padding:"12px 14px", display:"flex", alignItems:"center", gap:12,
                      animation: isNew ? "saleSlideIn 0.6s cubic-bezier(0.16,1,0.3,1) both, saleGlow 2.4s ease-out both" : "none",
                    }}>
                      {s.thumbnail && (
                        <img src={s.thumbnail} alt={s.item_name} style={{ width:38, height:38, borderRadius:7, objectFit:"contain", background:"#161616", flexShrink:0 }} onError={e => e.target.style.display="none"} />
                      )}
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ color:"#ddd", fontSize:12.5, fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.item_name || "Unknown"}</div>
                        <div style={{ color:accent, fontSize:14, fontWeight:800, fontFamily:"'Profa',sans-serif", lineHeight:1.3 }}>${usd}</div>
                        <div style={{ color:"#444", fontSize:11, marginTop:2 }}>
                          {(s.amount||0).toLocaleString()} R$ · {timeStr}
                          {s.pending && <span style={{ color:"#a16207", marginLeft:6 }}>pending</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {salesWarn && recentSales.length > 0 && (
                  <div style={{ color:"#444", fontSize:11, paddingTop:2 }}>Some accounts couldn't be reached right now.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "Analytics"   && <Analytics token={token} limits={limits} />}
        {tab === "Settings"    && <Settings token={token} tier={tier} limits={limits} theme={theme} onThemeChange={onThemeChange} />}
        {tab === "Accounts"    && <Accounts token={token} tier={tier} limits={limits} />}
        {tab === "Inventory"   && <Inventory token={token} limits={limits} />}
        {tab === "Calculator"  && <Calculator token={token} />}
        {tab === "Help"        && <HelpTab token={token} />}
      </div>

      {sec && !sec.hasUsername && (
        <UsernameModal
          token={token}
          onLogout={onLogout}
          onSaved={() => setSec(prev => ({ ...prev, hasUsername: true }))}
        />
      )}
      {sec && sec.hasUsername && !sec.has2fa && (
        <SecureAccountModal
          token={token}
          hasPassword={sec.hasPassword}
          isAdmin={sec.isAdmin}
          onLogout={onLogout}
          onSecured={() => setSec(prev => ({ ...prev, hasPassword: true, has2fa: true }))}
        />
      )}
    </div>
  );
}

const getStyles = (accent, accentBg, accentBorder) => ({
  wrap:       { display: "flex", height: "calc(100vh / var(--ui-zoom, 1))", overflow: "hidden", background: "#0a0a0a", color: "#fff", fontFamily: "'Rubik', 'Inter', sans-serif", position: "relative" },
  hamburger:  { position: "fixed", top: 14, left: 14, zIndex: 210, width: 40, height: 40, borderRadius: 8, background: "#161616", border: "1px solid #2a2a2a", color: "#fff", fontSize: 18, cursor: "pointer" },
  backdrop:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 150 },
  sidebar:    { width: 210, background: "#0f0f0f", borderRight: "1px solid #1a1a1a", display: "flex", flexDirection: "column", padding: "16px 0" },
  logoWrap:   { display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" },
  logoImg:    { width:140, objectFit:"contain", mixBlendMode:"screen" },
  tierBadge:  { fontSize: 10, color: "#666", padding: "0 20px 16px", letterSpacing: 2, fontFamily: "'Lumiare', monospace" },

  bottomRow:    { display: "flex", alignItems: "center", gap: 8, padding: "14px 20px 4px", borderTop: "1px solid #1a1a1a", marginTop: 8, flexWrap: "wrap" },
  discordIcon:  { display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: "#1a1a1a", color: "#888", textDecoration: "none" },
  tierLetter:   { position: "relative", width: 30, height: 30, borderRadius: 8, background: "#1a1a1a", color: "#888", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Moderniz', sans-serif", transition: "color 0.2s ease, background 0.2s ease" },
  tierTooltip:  { position: "absolute", bottom: "calc(100% + 8px)", left: 0, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#fff", whiteSpace: "nowrap", boxShadow: "0 8px 24px rgba(0,0,0,0.5)", zIndex: 20, fontFamily: "'Inter',sans-serif" },
  tierTooltipTier: { fontWeight: 700, fontSize: 13, marginBottom: 4, letterSpacing: 0.5 },
  tierTooltipRow:  { fontWeight: 600, fontSize: 12, marginBottom: 4 },
  tierTooltipSub:  { color: "#666", fontSize: 11 },
  tierTooltipUpgrade: { color: "var(--rbx-accent, #38bdf8)", fontSize: 10.5, fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: "1px solid #2a2a2a" },
  upgradeIcon:     { position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: "#1a1a1a", flexShrink: 0, textDecoration: "none" },
  upgradeIconImg:  { width: 18, height: 18, objectFit: "contain" },

  main:       { flex: 1, padding: 32, overflowY: "auto", overflowX: "hidden", minHeight: 0, minWidth: 0 },
  robuxBar:   { background: "#111", border: "1px solid #1a1a1a", borderRadius: 8, padding: "14px 20px", marginBottom: 20, display: "flex", flexDirection: "column", gap: 6, overflowX: "hidden" },
  robuxRow:   { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", rowGap: 4 },
  robuxLabel: { color: "#444", fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: 1 },
  robuxVal:   { color: "var(--rbx-accent, #38bdf8)", fontSize: 15, fontWeight: 700, fontFamily: "'Rubik', sans-serif" },
  robuxUSD:   { color: "#166534", fontSize: 15, fontWeight: 700, fontFamily: "'Rubik', sans-serif" },
  robuxSep:   { color: "#2a2a2a", fontSize: 14 },
  cards:      { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 12, marginBottom: 20 },
  card:       { background: "#111", border: "1px solid #1a1a1a", borderRadius: 8, padding: "16px 20px" },
  cardLabel:  { color: "#555", fontSize: 11, marginBottom: 6, letterSpacing: 1, fontFamily: "'Lumiare', monospace" },
  cardVal:    { color: "#fff", fontSize: 22, fontWeight: 700, fontFamily: "'Profa', sans-serif" },
  controls:   { display: "flex", gap: 10, marginBottom: 20 },
  btn:        { padding: "10px 24px", borderRadius: 8, color: "#000", fontSize: 14 },
  salesWrap:    { marginBottom: 20 },
  salesHeader:  { color: "#555", fontSize: 12, letterSpacing: 1, marginBottom: 10, textTransform: "uppercase" },
  salesRow:     { display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 },
  saleCard:     { flexShrink: 0, width: 180, background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 10, padding: 14 },
  saleThumb:    { width: "100%", aspectRatio: "1", objectFit: "contain", background: "#161616", borderRadius: 8, marginBottom: 10, display: "block" },
  saleUsdRow:   { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 10 },
  rapPctBadge:  { fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" },
  saleItemName: { color: "#ccc", fontSize: 12.5, fontWeight: 600, marginBottom: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  saleUsd:      { color: "#fff", fontSize: 19, fontWeight: 800, fontFamily: "'Profa', sans-serif" },
  saleStatRow:  { display: "flex", justifyContent: "space-between", marginBottom: 3 },
  saleStatLabel:{ color: "#444", fontSize: 10.5 },
  saleStatVal:  { color: "#999", fontSize: 10.5, fontVariantNumeric: "tabular-nums" },
  logsWrap:   { background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 8 },
  logsHeader: { padding: "10px 16px", borderBottom: "1px solid #1a1a1a", color: "#555", fontSize: 12, letterSpacing: 1, fontFamily: "'Lumiare', monospace" },
  logs:       { height: 400, overflowY: "auto", padding: 12 },
  logLine:    { display: "flex", gap: 10, marginBottom: 4, fontSize: 12, fontFamily: "monospace" },
  logTs:      { color: "#333", flexShrink: 0 },
});